import React from 'react';
import {TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {
  annotate,
  ContextualExpression,
  ContextualExpressions,
  EDistinct as ContextualEDistinct,
  getAnn,
  stripAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  selectCurrentComponent,
  selectCurrentPage,
} from '@packages/utilities/platform-selectors';
import {selectPageState} from '@packages/systems/designer/page';
import {getDynPages} from '@packages/shared/utils/PageTreeUtils';
import {selectCollectionState} from '@packages/systems/cms/client-selectors';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {getPageBindingContext} from '@packages/systems/dynamo/gateway';
import {DynListSortingSettings} from './DynListSortingSettings';
import {getDefaultForComponentProp} from '../dataTypeUtils';
import {
  getCollectionListPropTypeName,
  createCollectionListPropType,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

const selectPluginsFromState = (state: DesignerStores) =>
  state.DesignerStore.plugins;

export const CollectionListSortRender =
  ({collectionId}: {collectionId: string}) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    return (
      <CollectionListSortComponent
        expr={expr}
        dispatch={dispatch}
        collectionId={collectionId}
      />
    );
  };

const CollectionListSortComponent = ({
  expr,
  dispatch,
  collectionId,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
  collectionId: string;
}) => {
  const selectedCurrentPage = useDesignerSelector(selectCurrentPage);
  const currentPage =
    selectedCurrentPage === undefined ? null : selectedCurrentPage;
  const pageState = useDesignerSelector(selectPageState);
  const dynamicPages = getDynPages(pageState);
  const collectionPageForCollection = dynamicPages.find(
    (page) => page.dynCollection === collectionId
  );
  const collectionState = useDesignerSelector(selectCollectionState);
  const plugins = useDesignerSelector(selectPluginsFromState);
  const currentComponent = useDesignerSelector(selectCurrentComponent);
  if (!currentPage || !collectionPageForCollection) return null;
  const bindingContext = getPageBindingContext(
    collectionPageForCollection,
    collectionState.collections
  );

  const ann = getAnn(expr);
  const isBinding = ContextualExpressions.isBinding(stripAnn(expr));

  const defaultValueExpr = getDefaultForComponentProp(
    plugins,
    currentComponent,
    stripAll(expr)
  );

  // If expression is already a binding, use it as-is. Otherwise, wrap it in a Distinct
  // expression to ensure proper type checking and binding capabilities for the sort data.
  const exprAsUnique = isBinding
    ? expr
    : annotate(
        ContextualEDistinct({
          name: getCollectionListPropTypeName('Sort', collectionId),
          value: expr,
        }),
        {
          ...ann,
          env: {
            ...ann.env,
            // Ensure there is no duplicate `in distinct` key
            expressionPath: ann.env.expressionPath.slice(0, -1),
          },
          type: withSystem(
            {
              isBindable: true,
              label: 'Sort',
              labelVariant: 'hidden',
            },
            createCollectionListPropType('Sort', collectionId)
          ),
        }
      );

  return (
    <DynListSortingSettings
      expressionEditorDispatch={dispatch}
      bindingContext={bindingContext}
      plugins={plugins}
      expr={expr}
      exprAsUnique={exprAsUnique}
      defaultValueExpr={defaultValueExpr}
    />
  );
};
